"use client";

import { useState, type FormEvent } from "react";

const reasons = [
  { value: "", label: "Select a reason (optional)" },
  { value: "no_longer_needed", label: "I no longer need Skylight" },
  { value: "privacy", label: "Privacy concerns" },
  { value: "duplicate_account", label: "I have another account" },
  { value: "too_many_notifications", label: "Too many notifications" },
  { value: "bad_experience", label: "Bad experience with other users" },
  { value: "other", label: "Other" },
];

function buildReference() {
  const stamp = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `DEL-${stamp}-${rand}`;
}

export function AccountDeletionRequestForm() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const emailValue = email.trim();
    const phoneValue = phone.trim();
    if (!emailValue && !phoneValue) {
      setError("Enter the email address or phone number registered to the account.");
      return;
    }
    if (emailValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      setError("Enter a valid email address.");
      return;
    }
    if (!confirmed) {
      setError("Confirm that you understand deletion is permanent.");
      return;
    }

    const ref = buildReference();
    setReference(ref);

    // Frontend-only submission path: route the request to support by email so it
    // reaches the deletion queue until a dedicated backend endpoint ships.
    const lines = [
      `Account deletion request ${ref}`,
      fullName.trim() ? `Name: ${fullName.trim()}` : null,
      emailValue ? `Account email: ${emailValue}` : null,
      phoneValue ? `Account phone: ${phoneValue}` : null,
      reason ? `Reason: ${reasons.find((item) => item.value === reason)?.label ?? reason}` : null,
      details.trim() ? `Details: ${details.trim()}` : null,
      "I confirm I am the account owner and understand deletion is permanent.",
    ].filter(Boolean);
    const mail = new URL("mailto:support@skylight.co.ke");
    mail.searchParams.set("subject", `[account-deletion] ${ref}`);
    mail.searchParams.set("body", lines.join("\n"));
    window.location.href = mail.toString();
  }

  if (reference) {
    return (
      <section className="form-card" aria-live="polite">
        <h2>Deletion request submitted</h2>
        <p>
          Your reference number is <strong>{reference}</strong>. Keep it for follow-up.
        </p>
        <p>
          A pre-filled email to <strong>support@skylight.co.ke</strong> was opened in your mail
          app — please send it to complete the request. If it did not open, email us directly and
          include your reference number and the account email or phone number.
        </p>
        <p>
          We will verify ownership using the contact details registered to the account before
          processing. Deletion is completed within 30 days of verification.
        </p>
      </section>
    );
  }

  return (
    <section className="form-card">
      <h2>Deletion request form</h2>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="deletion-name">Full name (optional)</label>
          <input
            id="deletion-name"
            className="input"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            autoComplete="name"
          />
        </div>
        <div className="field">
          <label htmlFor="deletion-email">Account email address</label>
          <input
            id="deletion-email"
            className="input"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </div>
        <div className="field">
          <label htmlFor="deletion-phone">Account phone number (if no email)</label>
          <input
            id="deletion-phone"
            className="input"
            type="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+ country code and number"
            autoComplete="tel"
          />
        </div>
        <div className="field">
          <label htmlFor="deletion-reason">Reason for leaving</label>
          <select
            id="deletion-reason"
            className="select"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          >
            {reasons.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="deletion-details">Anything else we should know? (optional)</label>
          <textarea
            id="deletion-details"
            className="input"
            rows={4}
            value={details}
            onChange={(event) => setDetails(event.target.value)}
          />
        </div>
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />{" "}
            I am the account owner and I understand this permanently deletes my account, profile,
            listings, posts, videos, and messages, and cannot be undone.
          </label>
        </div>
        {error ? <p role="alert">{error}</p> : null}
        <button className="btn btn-primary" type="submit">
          Submit deletion request
        </button>
      </form>
    </section>
  );
}
